<template>
  <div class="step-page">
    <div class="step-header">
      <div class="step-badge">Projet : {{ projectName }}</div>
      <h1 class="step-title">Configuration de la toiture</h1>
      <p class="step-subtitle">
        Renseignez les caractéristiques de votre installation pour calculer la puissance PV installable.
      </p>
    </div>

    <div class="step-content">

      <!-- Module PV (toujours visible, pleine largeur) -->
      <div class="card-block card-block--full">
        <div class="card-block__title">Caractéristiques du module PV</div>
        <div class="form-col champs-courts">
          <div class="form-group">
            <label class="form-label">Puissance d'un module (Wc)</label>
            <input
              v-model.number="form.puissanceModuleWc"
              type="number"
              min="50"
              max="1000"
              step="5"
              class="form-input"
              placeholder="Ex : 490"
              @input="recalculate"
            />
          </div>
          <div class="form-group">
            <label class="form-label">Surface d'un module (m²)</label>
            <input
              v-model.number="form.tailleModuleM2"
              type="number"
              min="0.1"
              max="5"
              step="0.01"
              class="form-input"
              placeholder="Ex : 2.10"
              @input="recalculate"
            />
            <span class="form-hint">Surface physique d'un panneau</span>
          </div>
        </div>
      </div>

      <!-- Modèle de production solaire -->
      <div class="card-block card-block--full">
        <div class="card-block__title">Modèle de production solaire</div>

        <div class="model-selector__options">
          <label class="model-option" :class="{ 'model-option--active': form.modeleProduction === 'pvgis' }">
            <input type="radio" v-model="form.modeleProduction" value="pvgis" @change="onModeleChange" />
            <div class="model-option__content">
              <div class="model-option__title">Modèle PVGIS — Multi-toitures</div>
              <div class="model-option__desc">Données réelles d'irradiation depuis PVGIS — plusieurs toitures possibles</div>
            </div>
          </label>
          <label class="model-option" :class="{ 'model-option--active': form.modeleProduction === 'db' }">
            <input type="radio" v-model="form.modeleProduction" value="db" @change="onModeleChange" />
            <div class="model-option__content">
              <div class="model-option__title">Modèle Systèmes Solaires</div>
              <div class="model-option__desc">Modèle simplifié de Systèmes Solaires, avec productible fixe à 1 000 kWh/kWc</div>
            </div>
          </label>
        </div>

        <!-- Mode DB -->
        <div v-if="form.modeleProduction === 'db'" class="pvgis-section">
          <div class="toitures-list">
            <div class="toiture-card">
              <div class="toiture-card__header">
                <span class="toiture-card__index">Toiture</span>
                <span class="toiture-card__kwc">{{ form.puissanceTotaleKwc.toFixed(2) }} kWc</span>
              </div>
              <div class="toiture-card__fields">
                <div class="form-group">
                  <label class="form-label">Surface disponible (m²)</label>
                  <input
                    v-model.number="form.surfaceM2"
                    type="number"
                    min="1"
                    step="0.1"
                    class="form-input"
                    placeholder="Ex : 47"
                    @input="recalculate"
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">Inclinaison (°)</label>
                  <input
                    v-model.number="form.inclinaisonDeg"
                    type="number"
                    min="0"
                    max="90"
                    step="1"
                    class="form-input"
                    placeholder="Ex : 30"
                  />
                  <span class="form-hint">0° horizontal — 90° vertical</span>
                </div>
                <div class="form-group">
                  <label class="form-label">Type de pose</label>
                  <select v-model="form.mountingplace" class="form-input form-select">
                    <option value="roof">Surimposition sur toiture inclinée</option>
                    <option value="free">Au sol ou sur toiture plate</option>
                    <option value="building">Intégré au bâti sur toiture inclinée (BIPV)</option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">Orientation</label>
                  <select v-model="form.orientation" class="form-input form-select">
                    <option value="Sud">Sud</option>
                    <option value="Est-Ouest">Est-Ouest</option>
                    <option value="Est ou Ouest">Est ou Ouest</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div v-if="dbError" class="pvgis-error">{{ dbError }}</div>
        </div>

        <!-- Mode PVGIS multi-toitures -->
        <div v-if="form.modeleProduction === 'pvgis'" class="pvgis-section">
          <div v-if="!latitude || !longitude" class="pvgis-warning">
            Aucune coordonnée GPS définie en Étape 1. Renseignez l'adresse avec une localisation pour utiliser PVGIS.
          </div>
          <template v-else>
            <div class="toitures-header">
              <div class="toitures-hint">
                Définissez une ou plusieurs toitures. Le profil PVGIS est calculé par toiture puis combiné en fonction de la puissance installée sur chacune.
              </div>
              <div class="gps-info">
                <span class="gps-badge">GPS</span>
                Lat {{ latitude ? latitude.toFixed(4) : '—' }}° / Lon {{ longitude ? longitude.toFixed(4) : '—' }}°
              </div>
            </div>

            <div class="form-group pertes-systeme">
              <label class="form-label" for="pertes-systeme" title="Câbles, onduleur, salissures, écart entre panneaux, dégradation des premiers mois. Hors chaleur et angle du soleil, calculés par PVGIS.">Pertes du système (%)</label>
              <input id="pertes-systeme" v-model.number="form.pertesSysteme" type="number" min="0" max="50" step="0.5" class="form-input" />
              <span class="form-hint">PVGIS conseille 14 %. Le vieillissement des panneaux se règle à l'étape 4.</span>
            </div>

            <!-- Liste des toitures -->
            <div class="toitures-list">
              <div v-for="(toiture, idx) in form.toitures" :key="idx" class="toiture-card">
                <div class="toiture-card__header">
                  <span class="toiture-card__index">Toiture {{ idx + 1 }}</span>
                  <span class="toiture-card__kwc">{{ toitureKwc(toiture).toFixed(2) }} kWc</span>
                  <button
                    v-if="form.toitures.length > 1"
                    class="btn-remove"
                    @click="removeToiture(idx)"
                    title="Supprimer cette toiture"
                  >✕</button>
                </div>
                <div class="toiture-card__fields">
                  <div class="form-group">
                    <label class="form-label">Surface (m²)</label>
                    <input
                      v-model.number="toiture.surfaceM2"
                      type="number"
                      min="1"
                      step="0.1"
                      class="form-input"
                      placeholder="Ex : 30"
                      @input="recalculate"
                    />
                  </div>
                  <div class="form-group">
                    <label class="form-label">Inclinaison (°)</label>
                    <input
                      v-model.number="toiture.inclinaisonDeg"
                      type="number"
                      min="0"
                      max="90"
                      step="1"
                      class="form-input"
                      placeholder="Ex : 30"
                    />
                    <span v-if="erreurInclinaison(toiture)" class="champ-erreur">{{ erreurInclinaison(toiture) }}</span>
                  </div>
                  <div class="form-group">
                    <label class="form-label">Orientation (°)</label>
                    <input
                      v-model.number="toiture.orientationDegres"
                      type="number"
                      min="0"
                      max="360"
                      step="1"
                      class="form-input"
                      placeholder="180 = Sud, 90 = Est, 270 = Ouest"
                    />
                    <span v-if="erreurOrientation(toiture)" class="champ-erreur">{{ erreurOrientation(toiture) }}</span>
                    <span class="form-hint">Nord = 0°/360°, Est = 90°, Sud = 180°, Ouest = 270°</span>
                  </div>
                  <div class="form-group">
                    <label class="form-label">Type de pose</label>
                    <select v-model="toiture.mountingplace" class="form-input form-select">
                      <option value="roof">Surimposition sur toiture inclinée</option>
                      <option value="free">Au sol ou sur toiture plate</option>
                      <option value="building">Intégré au bâti sur toiture inclinée (BIPV)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="toitures-actions">
              <button class="btn-add-toiture" @click="addToiture">
                + Ajouter une toiture
              </button>
            </div>
            <div v-if="pvgisError" class="pvgis-error">{{ pvgisError }}</div>
          </template>
        </div>

        <!-- Indicateur puissance installable (mis à jour en temps réel) -->
        <div v-if="form.puissanceTotaleKwc > 0" class="puissance-summary">
          <span class="puissance-summary__label">&#9889; Puissance totale installable</span>
          <span class="puissance-summary__value">{{ form.puissanceTotaleKwc.toFixed(2) }} kWc</span>
        </div>

        <!-- Bouton de calcul unifié -->
        <div class="toitures-actions toitures-actions--calc">
          <button
            class="btn-import"
            :disabled="isCalculLoading || !canCalculer"
            @click="calculer"
          >
            <span v-if="isCalculLoading" class="btn-import__spinner"></span>
            <span v-else>Calculer</span>
          </button>
        </div>
      </div>

      <!-- Résultats du profil (après import DB ou PVGIS) -->
      <div v-if="activeStats" class="card-block card-block--full pvgis-result-block">
        <div class="card-block__title">Résultats du profil de production</div>

        <!-- KPI row -->
        <div class="kpi-row">
          <div class="kpi-card kpi-card--power">
            <div class="kpi-card__icon">&#9889;</div>
            <div class="kpi-card__body">
              <div class="kpi-card__label">Puissance totale installable</div>
              <div class="kpi-card__value">{{ form.puissanceTotaleKwc.toFixed(2) }} kWc</div>
              <div v-if="form.modeleProduction === 'db'" class="kpi-card__sub">
                {{ nbModulesDb }} modules × {{ form.puissanceModuleWc }} Wc
              </div>
              <div v-else class="kpi-card__sub">
                {{ form.toitures.length }} toiture(s) — {{ totalModulesPvgis }} modules × {{ form.puissanceModuleWc }} Wc
              </div>
            </div>
          </div>
          <div class="kpi-card kpi-card--solar">
            <div class="kpi-card__icon">&#9728;</div>
            <div class="kpi-card__body">
              <div class="kpi-card__label">Productible annuel</div>
              <div class="kpi-card__value">{{ activeStats.annualKwhPerKwc }} kWh/kWc</div>
              <div v-if="form.modeleProduction === 'pvgis'" class="kpi-card__sub">
                Source : PVGIS — Lat {{ latitude ? latitude.toFixed(3) : '—' }}° / Lon {{ longitude ? longitude.toFixed(3) : '—' }}°
              </div>
              <div v-else class="kpi-card__sub">
                Source : modèle Systèmes Solaires (1 000 kWh/kWc) — Orientation {{ form.orientation }}
              </div>
            </div>
          </div>
        </div>

        <!-- Tableau de répartition par toiture (mode PVGIS multi) -->
        <div v-if="form.modeleProduction === 'pvgis' && pvgisBreakdown.length > 1" class="breakdown-section">
          <div class="breakdown-title">Répartition par toiture</div>
          <div class="breakdown-table-wrap">
            <table class="breakdown-table">
              <thead>
                <tr>
                  <th>Toiture</th>
                  <th>Surface</th>
                  <th>Puissance</th>
                  <th>Productible</th>
                  <th>Production annuelle</th>
                  <th>Contribution</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in pvgisBreakdown" :key="b.index">
                  <td>Toiture {{ b.index }}</td>
                  <td>{{ b.surfaceM2.toFixed(0) }} m²</td>
                  <td>{{ b.kwc.toFixed(2) }} kWc</td>
                  <td>{{ b.producibleKwhPerKwc }} kWh/kWc</td>
                  <td>{{ Math.round(b.productionAnnuelleKwh) }} kWh/an</td>
                  <td>
                    <div class="contrib-bar-wrap">
                      <div class="contrib-bar" :style="{ width: b.contributionPct.toFixed(1) + '%' }"></div>
                      <span>{{ b.contributionPct.toFixed(1) }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Graphique -->
        <pv-chart
          v-if="form.modeleProduction === 'pvgis'"
          :pvgis-stats="activeStats"
          :api-base-endpoint="null"
          :extra-params="{}"
          :puissance-totale-kwc="form.puissanceTotaleKwc"
        />
        <pv-chart
          v-else
          :pvgis-stats="activeStats"
          :api-base-endpoint="'/wizard/db-profile'"
          :extra-params="{ orientation: form.orientation }"
          :puissance-totale-kwc="form.puissanceTotaleKwc"
        />
      </div>

    </div>

    <div class="step-footer">
      <button class="btn-back" @click="$emit('back')">Retour</button>
      <button class="btn-next" :disabled="!isValid" @click="submit">Étape suivante →</button>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import axios from 'axios'
import { authenticationService } from '@/services/authentication'
import { conf } from '@/config/config'
import PvChart from './PvChart.vue'

export default {
  name: 'Step2Toiture',
  components: { PvChart },
  data() {
    return {
      formReady: false,
      form: {
        surfaceM2: null,
        modeleProduction: 'pvgis',
        orientation: 'Sud',
        orientationDegres: 180,
        mountingplace: 'roof',
        pertesSysteme: 14,
        inclinaisonDeg: 30,
        puissanceModuleWc: 490,
        tailleModuleM2: 2.10,
        puissanceTotaleKwc: 0,
        toitures: [],
      },
      pvgisStats: null,
      pvgisLoading: false,
      pvgisError: null,
      pvgisBreakdown: [],
      dbStats: null,
      dbLoading: false,
      dbError: null,
    }
  },
  watch: {
    form: {
      deep: true,
      handler() { if (this.formReady) this.SET_DIRTY(true) },
    },
    // nouvelles pertes : le profil PVGIS déjà calculé ne vaut plus, il faut le recalculer
    'form.pertesSysteme'() {
      if (!this.formReady) return
      this.pvgisStats = null
      this.pvgisBreakdown = []
      this.SET_PRODUCTION_STATS({ pvgisStats: null, pvgisBreakdown: [] })
      this.SET_PV_PROFILE_ID(null)
    },
  },
  computed: {
    ...mapState('wizard', [
      'projectName', 'latitude', 'longitude',
      'surfaceM2', 'modeleProduction', 'orientation', 'orientationDegres', 'mountingplace', 'pertesSysteme',
      'inclinaisonDeg', 'puissanceModuleWc', 'tailleModuleM2', 'puissanceTotaleKwc',
      'toitures', 'savedPvgisStats', 'savedDbStats', 'savedPvgisBreakdown', 'pvProfileId',
    ]),
    nbModulesDb() {
      if (!this.form.surfaceM2 || !this.form.tailleModuleM2 || this.form.tailleModuleM2 <= 0) return 0
      return this.nbModules(this.form.surfaceM2)
    },
    totalModulesPvgis() {
      if (!this.form.tailleModuleM2 || this.form.tailleModuleM2 <= 0) return 0
      var total = 0
      for (var i = 0; i < this.form.toitures.length; i++) {
        if (this.form.toitures[i].surfaceM2 > 0) {
          total += this.nbModules(this.form.toitures[i].surfaceM2)
        }
      }
      return total
    },
    canCalculerPvgis() {
      if (this.form.toitures.length === 0) return false
      for (var i = 0; i < this.form.toitures.length; i++) {
        if (!this.form.toitures[i].surfaceM2 || this.form.toitures[i].surfaceM2 <= 0) return false
        if (this.erreurInclinaison(this.form.toitures[i]) || this.erreurOrientation(this.form.toitures[i])) return false
      }
      return true
    },
    isCalculLoading() {
      return this.form.modeleProduction === 'db' ? this.dbLoading : this.pvgisLoading
    },
    canCalculer() {
      if (this.form.modeleProduction === 'db') return !this.dbLoading
      return !this.pvgisLoading && this.canCalculerPvgis && !!this.latitude && !!this.longitude
    },
    isValid() {
      var baseOk = this.form.puissanceModuleWc > 0 && this.form.tailleModuleM2 > 0 && this.form.puissanceTotaleKwc > 0
      if (!baseOk) return false
      if (this.form.modeleProduction === 'db') {
        return this.form.surfaceM2 > 0 && !!this.activeStats
      }
      return this.form.toitures.length > 0 && !!this.pvgisStats && !!this.pvProfileId
    },
    activeStats() {
      return this.form.modeleProduction === 'pvgis' ? this.pvgisStats : this.dbStats
    },
  },
  mounted() {
    Object.assign(this.form, {
      surfaceM2: this.surfaceM2,
      modeleProduction: this.modeleProduction,
      orientation: this.orientation,
      orientationDegres: this.orientationDegres,
      mountingplace: this.mountingplace,
      pertesSysteme: this.pertesSysteme != null ? this.pertesSysteme : 14,
      inclinaisonDeg: this.inclinaisonDeg,
      puissanceModuleWc: this.puissanceModuleWc,
      tailleModuleM2: this.tailleModuleM2,
      puissanceTotaleKwc: this.puissanceTotaleKwc,
    })
    this.form.toitures = (this.toitures && this.toitures.length > 0)
      ? JSON.parse(JSON.stringify(this.toitures))
      : [this.newToiture()]
    if (this.form.surfaceM2 && this.form.modeleProduction === 'db') this.recalculate()
    if (this.savedPvgisStats) this.pvgisStats = this.savedPvgisStats
    if (this.savedDbStats) this.dbStats = this.savedDbStats
    if (this.savedPvgisBreakdown) this.pvgisBreakdown = this.savedPvgisBreakdown
    this.$nextTick(function() { this.formReady = true })
  },
  beforeDestroy() {
    // une réponse arrivée après le départ de l'étape est ignorée : le store est peut-être déjà celui d'une autre étude (22/10/2026)
    this.ecranQuitte = true
    this.SET_STEP2({
      surfaceM2: this.form.modeleProduction === 'db' ? this.form.surfaceM2 : null,
      toitures: this.form.modeleProduction === 'pvgis' ? JSON.parse(JSON.stringify(this.form.toitures)) : [],
      modeleProduction: this.form.modeleProduction,
      orientation: this.form.modeleProduction === 'db' ? this.form.orientation : 'Sud',
      orientationDegres: this.form.orientationDegres,
      mountingplace: this.form.mountingplace,
        pertesSysteme: this.form.pertesSysteme,
      inclinaisonDeg: this.form.inclinaisonDeg,
      puissanceModuleWc: this.form.puissanceModuleWc,
      tailleModuleM2: this.form.tailleModuleM2,
      puissanceTotaleKwc: this.form.puissanceTotaleKwc,
    })
  },
  methods: {
    ...mapMutations('wizard', ['SET_DIRTY', 'SET_STEP2', 'SET_PRODUCTION_STATS', 'SET_PV_PROFILE_ID']),
    newToiture() {
      return { surfaceM2: null, inclinaisonDeg: 30, orientationDegres: 180, mountingplace: 'roof' }
    },
    addToiture() {
      this.form.toitures.push(this.newToiture())
    },
    /** Inclinaison hors de 0 à 90° : message affiché sous le champ, calcul bloqué. */
    erreurInclinaison(toiture) {
      var v = toiture.inclinaisonDeg
      if (typeof v !== 'number' || isNaN(v) || v < 0 || v > 90) return 'Saisissez une inclinaison entre 0 et 90°.'
      return null
    },
    /** Orientation boussole hors de 0 à 360° (ex. − 30°, convention PVGIS) : refusée avec un message. */
    erreurOrientation(toiture) {
      var v = toiture.orientationDegres
      if (typeof v !== 'number' || isNaN(v) || v < 0 || v > 360) {
        return 'Saisissez une orientation entre 0 et 360° (Est = 90°, Sud = 180°, Ouest = 270°).'
      }
      return null
    },
    removeToiture(idx) {
      this.form.toitures.splice(idx, 1)
      this.recalculate()
    },
    /** Nombre de modules entiers sur une surface ; la tolérance évite 74,82 / 1,74 = 42,999… → 42 au lieu de 43. */
    nbModules(surfaceM2) {
      return Math.floor(surfaceM2 / this.form.tailleModuleM2 + 1e-9)
    },
    toitureKwc(toiture) {
      if (!toiture.surfaceM2 || !this.form.tailleModuleM2 || this.form.tailleModuleM2 <= 0) return 0
      var nb = this.nbModules(toiture.surfaceM2)
      return Math.round(nb * this.form.puissanceModuleWc) / 1000
    },
    recalculate() {
      if (this.form.modeleProduction === 'pvgis') {
        var total = 0
        for (var i = 0; i < this.form.toitures.length; i++) {
          total += this.toitureKwc(this.form.toitures[i])
        }
        this.form.puissanceTotaleKwc = Math.round(total * 100) / 100
      } else {
        var nb = this.nbModulesDb
        if (nb > 0 && this.form.puissanceModuleWc > 0) {
          this.form.puissanceTotaleKwc = Math.round(nb * this.form.puissanceModuleWc) / 1000
        } else {
          this.form.puissanceTotaleKwc = 0
        }
      }
    },
    calculer() {
      if (this.form.modeleProduction === 'db') {
        this.importerDb()
      } else {
        this.calculerPvgisMulti()
      }
    },
    onModeleChange() {
      this.pvgisStats = null
      this.pvgisError = null
      this.pvgisBreakdown = []
      this.dbStats = null
      this.dbError = null
      this.SET_PRODUCTION_STATS({ pvgisStats: null, dbStats: null, pvgisBreakdown: [] })
      this.SET_PV_PROFILE_ID(null)
      // S'assurer qu'il y a au moins une toiture vide en mode PVGIS
      if (this.form.modeleProduction === 'pvgis' && this.form.toitures.length === 0) {
        this.form.toitures = [this.newToiture()]
      }
      this.recalculate()
    },
    async importerDb() {
      this.dbLoading = true
      this.dbError = null
      this.dbStats = null
      try {
        var token = authenticationService.status.jwtToken
        var resp = await axios.get(conf.API_URL + '/wizard/db-profile', {
          params: { orientation: this.form.orientation },
          headers: { Authorization: 'Bearer ' + token },
        })
        if (this.ecranQuitte) return
        if (resp.data && resp.data.data) {
          this.dbStats = resp.data.data
          this.SET_PRODUCTION_STATS({ dbStats: resp.data.data })
        } else {
          this.dbError = 'Profil DB indisponible'
        }
      } catch (e) {
        var msg = 'Erreur lors du chargement du profil'
        if (e && e.response && e.response.data && e.response.data.message) {
          msg = e.response.data.message
        }
        this.dbError = msg
      } finally {
        this.dbLoading = false
      }
    },
    async calculerPvgisMulti() {
      if (!this.latitude || !this.longitude || !this.canCalculerPvgis) return
      this.pvgisLoading = true
      this.pvgisError = null
      this.pvgisStats = null
      this.pvgisBreakdown = []
      try {
        var token = authenticationService.status.jwtToken
        var toituresPayload = []
        for (var i = 0; i < this.form.toitures.length; i++) {
          var t = this.form.toitures[i]
          toituresPayload.push({
            lat: this.latitude,
            lon: this.longitude,
            tilt: t.inclinaisonDeg,
            aspect: t.orientationDegres - 180,
            mountingplace: t.mountingplace,
            kwc: this.toitureKwc(t),
            surfaceM2: t.surfaceM2,
            pertes: this.form.pertesSysteme,
          })
        }
        var resp = await axios.post(conf.API_URL + '/wizard/pv-profile/multi', {
          toitures: toituresPayload,
          oldProfileId: this.pvProfileId || null,
        }, {
          headers: { Authorization: 'Bearer ' + token },
        })
        if (this.ecranQuitte) return
        if (resp.data && resp.data.data) {
          var result = resp.data.data
          this.pvgisStats = result.combined
          this.pvgisBreakdown = result.breakdown || []
          this.SET_PRODUCTION_STATS({
            pvgisStats: result.combined,
            pvgisBreakdown: result.breakdown || [],
          })
          this.SET_PV_PROFILE_ID(result.profileId)
        } else {
          this.pvgisError = 'Données PVGIS indisponibles'
        }
      } catch (e) {
        var msg = 'Erreur lors du calcul PVGIS'
        if (e && e.response && e.response.data && e.response.data.message) {
          msg = e.response.data.message
        }
        this.pvgisError = msg
      } finally {
        this.pvgisLoading = false
      }
    },
    async submit() {
      if (!this.isValid) return
      // Mode DB : sauvegarde du profil en DB avant de passer
      if (this.form.modeleProduction === 'db' && this.activeStats) {
        await this.savePvProfileDb()
        if (this.ecranQuitte) return
      }
      // Mode PVGIS : le profil est déjà sauvegardé lors de calculerPvgisMulti (pvProfileId est set)
      this.SET_STEP2({
        surfaceM2: this.form.modeleProduction === 'db' ? this.form.surfaceM2 : null,
        toitures: this.form.modeleProduction === 'pvgis' ? JSON.parse(JSON.stringify(this.form.toitures)) : [],
        modeleProduction: this.form.modeleProduction,
        orientation: this.form.modeleProduction === 'db' ? this.form.orientation : 'Sud',
        orientationDegres: this.form.orientationDegres,
        mountingplace: this.form.mountingplace,
        pertesSysteme: this.form.pertesSysteme,
        inclinaisonDeg: this.form.inclinaisonDeg,
        puissanceModuleWc: this.form.puissanceModuleWc,
        tailleModuleM2: this.form.tailleModuleM2,
        puissanceTotaleKwc: this.form.puissanceTotaleKwc,
      })
      this.$emit('next')
    },
    async savePvProfileDb() {
      try {
        var token = authenticationService.status.jwtToken
        var resp = await axios.post(conf.API_URL + '/wizard/pv-profile', {
          modele: 'db',
          orientation: this.form.orientation,
          oldProfileId: this.pvProfileId || null,
        }, {
          headers: { Authorization: 'Bearer ' + token },
        })
        if (this.ecranQuitte) return
        if (resp.data && resp.data.data) {
          this.SET_PV_PROFILE_ID(resp.data.data)
        }
      } catch (e) {
        console.warn('Impossible de sauvegarder le profil PV DB:', e)
      }
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Raleway:400,600,700&display=swap');

.step-page {
  font-family: 'Raleway', sans-serif;
  background: #f5f5f5;
  min-height: calc(100vh - 140px);
  padding: 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.step-header { display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 4px; }

.step-badge {
  display: inline-block;
  background: #f5c518;
  color: #222;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
}

.step-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 6px 0;
}

.step-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card-block {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-block--full { width: 100%; }

.card-block__title {
  font-size: 15px;
  font-weight: 700;
  color: #222;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f5c518;
  display: inline-block;
}

.form-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.form-input {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  color: #222;
  outline: none;
  transition: border-color 0.15s;
}

.form-input:focus {
  border-color: #f5c518;
  box-shadow: 0 0 0 2px rgba(245, 197, 24, 0.2);
}

.form-select {
  appearance: none;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat right 12px center #fff;
  cursor: pointer;
}

.form-hint { font-size: 12px; color: #6b7280; } /* plus lisible (13/10/2026) */
.champ-erreur { font-size: 12px; color: #c0392b; }

/* Sélection modèle */
.model-selector__options {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.model-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  flex: 1;
  min-width: 220px;
  transition: border-color 0.15s, background 0.15s;
}

.model-option input[type="radio"] {
  margin-top: 2px;
  accent-color: #f5c518;
  flex-shrink: 0;
}

.model-option--active {
  border-color: #f5c518;
  background: #fffde7;
}

.model-option__title {
  font-size: 14px;
  font-weight: 700;
  color: #222;
  margin-bottom: 3px;
}

.model-option__desc { font-size: 12px; color: #888; line-height: 1.4; }

/* DB mode */
.orientation-db { max-width: 360px; }

/* PVGIS section */
.pvgis-section { display: flex; flex-direction: column; gap: 16px; }

.pvgis-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #856404;
}

.pvgis-controls {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.pvgis-controls .form-group { min-width: 280px; }

.toitures-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.toitures-hint {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
  flex: 1;
}

.gps-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #888;
  white-space: nowrap;
}

.gps-badge {
  background: #e8f5e9;
  color: #2e7d32;
  font-weight: 700;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
}

/* Toiture cards */
.toitures-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toiture-card {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
}

.toiture-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #fafafa;
  border-bottom: 1px solid #eee;
}

.toiture-card__index {
  font-weight: 700;
  font-size: 13px;
  color: #1a1a2e;
  flex: 1;
}

.toiture-card__kwc {
  font-size: 13px;
  font-weight: 700;
  color: #e67e00;
  background: #fff8e1;
  padding: 3px 10px;
  border-radius: 12px;
}

.btn-remove {
  background: none;
  border: none;
  color: #bbb;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s;
}

.btn-remove:hover { color: #d32f2f; background: #fdecea; }

.toiture-card__fields {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 16px;
}

@media (max-width: 900px) {
  .toiture-card__fields { grid-template-columns: 1fr 1fr; }
}

/* Actions bar */
.toitures-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.toitures-actions--calc {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}
.puissance-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 8px;
  padding: 10px 16px;
  margin-top: 16px;
}
.puissance-summary__label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}
.puissance-summary__value {
  font-size: 18px;
  font-weight: 800;
  color: #1a1a2e;
}

.btn-add-toiture {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border: 1.5px dashed #f5c518;
  border-radius: 24px;
  background: transparent;
  color: #222;
  font-size: 13px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.btn-add-toiture:hover { background: #fffde7; }

.btn-import {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 28px;
  border: none;
  border-radius: 24px;
  background: #f5c518;
  color: #222;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
  height: 42px;
}

.btn-import:hover:not(:disabled) { background: #e6b800; }
.btn-import:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-import__spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0,0,0,0.2);
  border-top-color: #222;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.pvgis-error {
  background: #fdecea;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #721c24;
}

/* Bloc résultat */
.pvgis-result-block { border-top: 3px solid #e67e00; }

/* KPIs */
.kpi-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  border-radius: 10px;
  padding: 14px 18px;
}

.kpi-card--power {
  background: linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%);
  border: 1.5px solid #f5c518;
}

.kpi-card--solar {
  background: linear-gradient(135deg, #fff8e1 0%, #fff3e0 100%);
  border: 1.5px solid #ffe0b2;
}

.kpi-card__icon { font-size: 30px; flex-shrink: 0; }

.kpi-card__label {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 3px;
}

.kpi-card--power .kpi-card__value { color: #1a1a2e; font-size: 22px; font-weight: 800; }
.kpi-card--solar .kpi-card__value { color: #e67e00; font-size: 22px; font-weight: 800; }
.kpi-card__sub { font-size: 11px; color: #aaa; margin-top: 2px; }

/* Breakdown table */
.breakdown-section { margin-bottom: 20px; }

.breakdown-title {
  font-size: 13px;
  font-weight: 700;
  color: #555;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.breakdown-table-wrap { overflow-x: auto; }

.breakdown-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.breakdown-table th {
  background: #f7f7f7;
  font-weight: 700;
  color: #555;
  padding: 8px 12px;
  text-align: left;
  border-bottom: 2px solid #eee;
  white-space: nowrap;
}

.breakdown-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
}

.breakdown-table tr:last-child td { border-bottom: none; }

.contrib-bar-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.contrib-bar {
  height: 8px;
  background: #f5c518;
  border-radius: 4px;
  min-width: 4px;
  max-width: 120px;
  transition: width 0.3s;
}

/* Footer */
.step-footer {
  display: flex;
  gap: 12px;
  padding-top: 8px;
}

.btn-back {
  padding: 11px 28px;
  border: 1px solid #ccc;
  border-radius: 24px;
  background: #fff;
  color: #555;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s;
}

.btn-back:hover { border-color: #999; }

.btn-next {
  padding: 11px 32px;
  border: none;
  border-radius: 24px;
  background: #f5c518;
  color: #222;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}

.btn-next:hover:not(:disabled) { background: #e6b800; }
.btn-next:disabled { opacity: 0.4; cursor: not-allowed; }
/* champs courts sur une ligne, largeur adaptée aux valeurs (13/10/2026) */
.champs-courts { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 260px)); gap: 0 24px; }
.pertes-systeme .form-input { max-width: 200px; }
</style>
